/* =========================================================================
   CONFIG
   ========================================================================= */

const MIN_SIDE_KM = 3, MAX_SIDE_KM = 500, DEFAULT_SIDE_KM = 10;
const INCHES_TO_MM = 25.4;
const DEFAULT_W_IN = 10, DEFAULT_H_IN = 10;
const MIN_DIM_IN = 4, MAX_DIM_IN = 20, STEP_IN = 1;
const MIN_DIM_MM = 100, MAX_DIM_MM = 500, STEP_MM = 10;

const SHALLOW_SMOOTH_MM = 10;
const BAND_1_MM = 2.5;
const BAND_2_MM = 6;
const BAND_3_MM = 12;

const MAJOR_ROAD_CLASSES = ['motorway', 'trunk', 'primary', 'secondary'];
const MINOR_ROAD_CLASSES = ['tertiary', 'minor', 'service'];

const CLIPPER_SCALE = 100;

// Google Fonts TTF URLs for opentype.js text-to-path conversion
const FONT_URLS = {
  "'Great Vibes',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/greatvibes/GreatVibes-Regular.ttf',
  "'Dancing Script',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/dancingscript/DancingScript%5Bwght%5D.ttf',
  "Parisienne,cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/parisienne/Parisienne-Regular.ttf',
  "Sacramento,cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/sacramento/Sacramento-Regular.ttf',
  "'Pinyon Script',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/pinyonscript/PinyonScript-Regular.ttf',
  "'Cormorant Garamond',serif": 'https://raw.githubusercontent.com/google/fonts/main/ofl/cormorantgaramond/CormorantGaramond%5Bwght%5D.ttf',
  "Cinzel,serif": 'https://raw.githubusercontent.com/google/fonts/main/ofl/cinzel/Cinzel%5Bwght%5D.ttf',
  "'Alex Brush',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/alexbrush/AlexBrush-Regular.ttf',
  "Inter,Helvetica,sans-serif": 'https://raw.githubusercontent.com/google/fonts/main/ofl/inter/Inter%5Bopsz%2Cwght%5D.ttf',
};
const loadedFonts = {};

async function loadFont(fontFamily) {
  if (loadedFonts[fontFamily]) return loadedFonts[fontFamily];
  const url = FONT_URLS[fontFamily];
  if (!url || typeof opentype === 'undefined') return null;
  try {
    console.log('Loading font:', url);
    const font = await opentype.load(url);
    loadedFonts[fontFamily] = font;
    return font;
  } catch (e) {
    console.warn('Font load failed:', fontFamily, e);
    return null;
  }
}

// Convert text to ClipperLib polygon paths using opentype.js
// Measure text width using a hidden SVG element (matches actual SVG rendering)
const _measureSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
_measureSvg.setAttribute('style', 'position:absolute;visibility:hidden;width:0;height:0;overflow:hidden;');
const _measureTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
_measureSvg.appendChild(_measureTxt);
document.addEventListener('DOMContentLoaded', () => document.body.appendChild(_measureSvg));
if (document.body) document.body.appendChild(_measureSvg);

function measureTextWidth(text, fontFamily, fontSize, fontWeight) {
  _measureTxt.setAttribute('font-family', fontFamily);
  _measureTxt.setAttribute('font-size', fontSize);
  _measureTxt.setAttribute('font-weight', fontWeight || '800');
  _measureTxt.textContent = text;
  return _measureTxt.getComputedTextLength();
}

function textToPolygons(text, font, fontSize, x, y) {
  if (!font || !text) return [];
  const path = font.getPath(text, x, y, fontSize);
  // Collect all glyph rings, then UNION them to eliminate overlap gaps
  // Convert opentype path commands to polygon rings
  const rings = [];
  let current = [];
  for (const cmd of path.commands) {
    if (cmd.type === 'M') {
      if (current.length >= 3) rings.push(current);
      current = [{ x: cmd.x, y: cmd.y }];
    } else if (cmd.type === 'L') {
      current.push({ x: cmd.x, y: cmd.y });
    } else if (cmd.type === 'C') {
      // Approximate cubic bezier with line segments
      const prev = current[current.length - 1] || { x: 0, y: 0 };
      for (let t = 0.2; t <= 1; t += 0.2) {
        const u = 1 - t;
        current.push({
          x: u*u*u*prev.x + 3*u*u*t*cmd.x1 + 3*u*t*t*cmd.x2 + t*t*t*cmd.x,
          y: u*u*u*prev.y + 3*u*u*t*cmd.y1 + 3*u*t*t*cmd.y2 + t*t*t*cmd.y,
        });
      }
    } else if (cmd.type === 'Q') {
      const prev = current[current.length - 1] || { x: 0, y: 0 };
      for (let t = 0.25; t <= 1; t += 0.25) {
        const u = 1 - t;
        current.push({
          x: u*u*prev.x + 2*u*t*cmd.x1 + t*t*cmd.x,
          y: u*u*prev.y + 2*u*t*cmd.y1 + t*t*cmd.y,
        });
      }
    } else if (cmd.type === 'Z') {
      if (current.length >= 3) rings.push(current);
      current = [];
    }
  }
  if (current.length >= 3) rings.push(current);
  // Union all rings to merge overlapping letters (prevents gaps in script fonts)
  if (rings.length > 1) {
    try {
      const merged = unionPolygons(rings);
      return merged.length ? merged : rings;
    } catch(e) { return rings; }
  }
  return rings;
}
const EARTH_RADIUS_M = 6371000;


/* =========================================================================
   UTILITIES
   ========================================================================= */

async function fetchWithTimeout(url, options = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

/* =========================================================================
   GEOCODING — Photon (primary) + Nominatim (fallback)
   ========================================================================= */

// Calculates the approximate km span of a bounding box
function extentToKm(south, north, west, east) {
  const latMid = (south + north) / 2;
  const kmLat = (north - south) * 110.574;
  const kmLon = (east - west) * 111.320 * Math.cos(latMid * Math.PI / 180);
  return { width: Math.abs(kmLon), height: Math.abs(kmLat) };
}

function buildDisplayName(props) {
  const parts = [props.name, props.city, props.state, props.country].filter(Boolean);
  return [...new Set(parts)].join(', ') || 'Unknown location';
}

async function tryPhoton(placeName, timeoutMs) {
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(placeName)}&limit=1`;
  const res = await fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, timeoutMs);
  if (!res.ok) throw new Error(`Photon HTTP ${res.status}`);
  const data = await res.json();
  if (!data.features || !data.features.length) return null;
  const feat = data.features[0];
  const [lon, lat] = feat.geometry.coordinates;
  // Photon returns extent: [west, south, east, north] for area features
  const ext = feat.properties.extent;
  const extentKm = ext ? extentToKm(ext[1], ext[3], ext[0], ext[2]) : null;
  return { lat, lon, displayName: buildDisplayName(feat.properties), extentKm };
}

async function tryNominatim(placeName, timeoutMs) {
  const params = new URLSearchParams({ format: 'json', limit: '1', q: placeName, email: 'maps@duperlab.com' });
  const res = await fetchWithTimeout(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'Accept': 'application/json' } }, timeoutMs);
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
  const results = await res.json();
  if (!results.length) return null;
  const r = results[0];
  // Nominatim returns boundingbox: [south, north, west, east]
  const bb = r.boundingbox;
  const extentKm = bb ? extentToKm(parseFloat(bb[0]), parseFloat(bb[1]), parseFloat(bb[2]), parseFloat(bb[3])) : null;
  return { lat: parseFloat(r.lat), lon: parseFloat(r.lon), displayName: r.display_name, extentKm };
}

async function geocodePlace(placeName, onStatus) {
  const providers = [
    { name: 'Photon', fn: tryPhoton },
    { name: 'Nominatim', fn: tryNominatim },
  ];
  let lastError = null;
  for (let i = 0; i < providers.length; i++) {
    const { name, fn } = providers[i];
    if (i > 0 && onStatus) onStatus(`Trying ${name}…`);
    try {
      const result = await fn(placeName, 10000);
      if (result) return result;
      throw new Error(`Couldn't find "${placeName}". Try a more specific name.`);
    } catch (err) {
      if (err.message && err.message.startsWith("Couldn't find")) throw err;
      lastError = err;
      continue;
    }
  }
  const detail = lastError ? ` (${lastError.name || 'Error'}: ${lastError.message || 'unknown'})` : '';
  if (location.protocol === 'file:') {
    throw new Error(`Browser is blocking network requests from a local file.${detail} Try Firefox, or run: python3 -m http.server`);
  }
  throw new Error(`Could not reach any place-lookup server.${detail}`);
}

/* =========================================================================
   VECTOR TILE LOADING (MapLibre GL JS + OpenFreeMap)
   Pre-built, CDN-cached tiles — loads in ~1-2 seconds vs 10-30s for
   Overpass, and never times out because there's no server-side query.
   ========================================================================= */


function makeProjector(centerLat, centerLon) {
  const phi0 = (centerLat * Math.PI) / 180;
  const lambda0 = (centerLon * Math.PI) / 180;
  const n = Math.sin(phi0);
  const F = Math.cos(phi0) * Math.pow(Math.tan(Math.PI / 4 + phi0 / 2), n) / n;
  const rho0 = EARTH_RADIUS_M * F / Math.pow(Math.tan(Math.PI / 4 + phi0 / 2), n);

  return function project(lat, lon) {
    const phi = (lat * Math.PI) / 180;
    const lambda = (lon * Math.PI) / 180;
    const rho = EARTH_RADIUS_M * F / Math.pow(Math.tan(Math.PI / 4 + phi / 2), n);
    const theta = n * (lambda - lambda0);
    const x = rho * Math.sin(theta);
    const y = rho0 - rho * Math.cos(theta);
    return { x, y: -y };
  };
}

/* =========================================================================
   GEOMETRY — ClipperLib helpers (mm, scaled to integers)
   ========================================================================= */

function mmToClipper(points) {
  return points.map((p) => ({ X: Math.round(p.x * CLIPPER_SCALE), Y: Math.round(p.y * CLIPPER_SCALE) }));
}
function clipperToMm(paths) {
  return paths.map((path) => path.map((p) => ({ x: p.X / CLIPPER_SCALE, y: p.Y / CLIPPER_SCALE })));
}

function unionPolygons(polygonsMm) {
  if (!polygonsMm.length) return [];
  const clipper = new ClipperLib.Clipper();
  clipper.AddPaths(polygonsMm.map(mmToClipper), ClipperLib.PolyType.ptSubject, true);
  const solution = new ClipperLib.Paths();
  clipper.Execute(ClipperLib.ClipType.ctUnion, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return clipperToMm(solution);
}

function differencePolygons(subjectMm, clipMm) {
  if (!subjectMm.length) return [];
  const clipper = new ClipperLib.Clipper();
  clipper.AddPaths(subjectMm.map(mmToClipper), ClipperLib.PolyType.ptSubject, true);
  if (clipMm.length) clipper.AddPaths(clipMm.map(mmToClipper), ClipperLib.PolyType.ptClip, true);
  const solution = new ClipperLib.Paths();
  clipper.Execute(ClipperLib.ClipType.ctDifference, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return clipperToMm(solution);
}

function intersectPolygons(subjectMm, clipMm) {
  if (!subjectMm.length || !clipMm.length) return [];
  const clipper = new ClipperLib.Clipper();
  clipper.AddPaths(subjectMm.map(mmToClipper), ClipperLib.PolyType.ptSubject, true);
  clipper.AddPaths(clipMm.map(mmToClipper), ClipperLib.PolyType.ptClip, true);
  const solution = new ClipperLib.Paths();
  clipper.Execute(ClipperLib.ClipType.ctIntersection, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return clipperToMm(solution);
}

function cleanupPolygons(polygonsMm) {
  if (!polygonsMm.length) return [];
  const scaled = polygonsMm.map(mmToClipper);
  const simplified = ClipperLib.Clipper.SimplifyPolygons(scaled, ClipperLib.PolyFillType.pftNonZero);
  const cleaned = ClipperLib.Clipper.CleanPolygons(simplified, 0.15 * CLIPPER_SCALE);
  return clipperToMm(cleaned);
}

function offsetPolygons(polygonsMm, deltaMm) {
  if (!polygonsMm.length) return [];
  const offset = new ClipperLib.ClipperOffset();
  offset.AddPaths(polygonsMm.map(mmToClipper), ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
  const solution = new ClipperLib.Paths();
  offset.Execute(solution, deltaMm * CLIPPER_SCALE);
  return clipperToMm(solution);
}

/* -------------------------------------------------------------------------
   ORGANIC SHALLOW/DEEP BOUNDARY
   Smooth sine-wave wobble applied to the clean offset, then rendered with
   Catmull-Rom curves (see ringToSmoothPathD) for a flowy contour.
   ------------------------------------------------------------------------- */

// --- 2D SPATIAL NOISE ---
// Each vertex is displaced based on its (x,y) position, NOT its distance
// along the perimeter. This makes each band's contour an independent
// artistic shape rather than a uniform offset of the coastline.

function hashNoise(ix, iy, seed) {
  let h = (ix * 374761393 + iy * 668265263 + seed * 1274126177) | 0;
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return ((h ^ (h >>> 16)) & 0x7fffffff) / 0x7fffffff * 2 - 1;
}

function noise2D(x, y, seed, scale) {
  const sx = x / scale, sy = y / scale;
  const ix = Math.floor(sx), iy = Math.floor(sy);
  const fx = sx - ix, fy = sy - iy;
  const u = fx * fx * (3 - 2 * fx);
  const v = fy * fy * (3 - 2 * fy);
  const a = hashNoise(ix, iy, seed), b = hashNoise(ix + 1, iy, seed);
  const c = hashNoise(ix, iy + 1, seed), d = hashNoise(ix + 1, iy + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fractalNoise2D(x, y, seed, baseScale, octaves) {
  let val = 0, amp = 1, total = 0;
  for (let i = 0; i < octaves; i++) {
    val += noise2D(x, y, seed + i * 997, baseScale / (1 << i)) * amp;
    total += amp;
    amp *= 0.35; // lower persistence = smoother, more organic shapes
  }
  return val / total;
}

function pathLength(points) {
  let len = 0;
  for (let i = 1; i < points.length; i++) len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  return len;
}

function polygonCentroid(points) {
  let x = 0, y = 0;
  for (const p of points) { x += p.x; y += p.y; }
  return { x: x / points.length, y: y / points.length };
}

// Displaces each vertex along its local normal by a value from the 2D noise
// field. `noiseScale` controls feature size (larger = broader undulations).
// `amplitude` controls how far the contour deviates from the uniform offset.
function displaceRingWithNoise(ring, amplitude, seed, noiseScale) {
  const centroid = polygonCentroid(ring);
  return ring.map((curr, i) => {
    const prev = ring[(i - 1 + ring.length) % ring.length];
    const next = ring[(i + 1) % ring.length];
    const tx = next.x - prev.x, ty = next.y - prev.y;
    const tLen = Math.hypot(tx, ty) || 1;
    let nx = -ty / tLen, ny = tx / tLen;
    const towardCentroidDot = nx * (centroid.x - curr.x) + ny * (centroid.y - curr.y);
    if (towardCentroidDot > 0) { nx = -nx; ny = -ny; }
    const n = fractalNoise2D(curr.x, curr.y, seed, noiseScale, 4) * amplitude;
    return { x: curr.x + nx * n, y: curr.y + ny * n };
  });
}

function displacePolygonsWithNoise(polygons, amplitude, seed, noiseScale) {
  return polygons.map((ring) => displaceRingWithNoise(ring, amplitude, seed, noiseScale));
}

// Chaikin's corner-cutting: produces genuinely smooth, organic curves
// from angular ClipperLib polygons. Each iteration doubles the vertex
// count and rounds all corners into flowing curves.
function chaikinSmooth(ring, iterations) {
  let r = ring;
  for (let iter = 0; iter < iterations; iter++) {
    const n = [];
    for (let i = 0; i < r.length; i++) {
      const a = r[i], b = r[(i + 1) % r.length];
      n.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      n.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    r = n;
  }
  return r;
}

function chaikinSmoothPolygons(polygons, iterations) {
  return polygons.map(ring => chaikinSmooth(ring, iterations));
}

// Removes tiny polygon fragments (corner artifacts, slivers from boolean ops)
function filterTinyPolygons(polygons, minAreaMmSq) {
  return polygons.filter(ring => {
    let area = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      area += a.x * b.y - b.x * a.y;
    }
    return Math.abs(area / 2) >= minAreaMmSq;
  });
}

// Adds intermediate vertices along long edges so Catmull-Rom has evenly
// spaced control points. Without this, tile-edge geometry with few vertices
// produces long flat sections followed by tight bends — the opposite of
// the smooth, flowing contour we want.
function resampleRing(ring, maxGap) {
  if (ring.length < 3) return ring;
  const out = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    out.push(a);
    const dx = b.x - a.x, dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    if (dist > maxGap) {
      const steps = Math.ceil(dist / maxGap);
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        out.push({ x: a.x + dx * t, y: a.y + dy * t });
      }
    }
  }
  return out;
}

function bufferLinesToPolygons(linesMm, widthMm) {
  if (!linesMm.length) return [];
  const offset = new ClipperLib.ClipperOffset();
  const paths = linesMm.map(mmToClipper).filter((p) => p.length >= 2);
  offset.AddPaths(paths, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
  const solution = new ClipperLib.Paths();
  offset.Execute(solution, (widthMm / 2) * CLIPPER_SCALE);
  return unionPolygons(clipperToMm(solution));
}

// Liang-Barsky clip of a single segment against [-halfW,halfW] × [-halfH,halfH].
function clipSegmentToRect(p0, p1, halfW, halfH) {
  let t0 = 0, t1 = 1;
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  const checks = [[-dx, p0.x + halfW], [dx, halfW - p0.x], [-dy, p0.y + halfH], [dy, halfH - p0.y]];
  for (const [p, q] of checks) {
    if (p === 0) { if (q < 0) return null; continue; }
    const r = q / p;
    if (p < 0) { if (r > t1) return null; if (r > t0) t0 = r; }
    else { if (r < t0) return null; if (r < t1) t1 = r; }
  }
  if (t0 > t1) return null;
  return { p0: { x: p0.x + t0 * dx, y: p0.y + t0 * dy }, p1: { x: p0.x + t1 * dx, y: p0.y + t1 * dy }, t1 };
}

function clipPolylineToRect(points, halfW, halfH) {
  const subpaths = [];
  let current = [];
  for (let i = 0; i < points.length - 1; i++) {
    const clipped = clipSegmentToRect(points[i], points[i + 1], halfW, halfH);
    if (!clipped) { if (current.length) { subpaths.push(current); current = []; } continue; }
    const last = current[current.length - 1];
    if (!last || Math.abs(last.x - clipped.p0.x) > 1e-6 || Math.abs(last.y - clipped.p0.y) > 1e-6) {
      if (current.length) subpaths.push(current);
      current = [clipped.p0];
    }
    current.push(clipped.p1);
    if (clipped.t1 < 1) { subpaths.push(current); current = []; }
  }
  if (current.length) subpaths.push(current);
  return subpaths;
}



/* =========================================================================
   PIPELINE
   ========================================================================= */

function tessellateSmooth(ring, segsPerEdge) {
  const n = ring.length;
  if (n < 3) return ring;
  const at = (i) => ring[((i % n) + n) % n];
  const out = [];
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    for (let s = 0; s < segsPerEdge; s++) {
      const t = s / segsPerEdge, u = 1 - t;
      out.push({
        x: u*u*u*p1.x + 3*u*u*t*c1x + 3*u*t*t*c2x + t*t*t*p2.x,
        y: u*u*u*p1.y + 3*u*u*t*c1y + 3*u*t*t*c2y + t*t*t*p2.y,
      });
    }
  }
  return out;
}
function tessellatePolygons(polygons, segs) { return polygons.map(r => tessellateSmooth(r, segs || 4)); }

function extractPolygonsMm(features, project, toMm) {
  const polygons = [];
  for (const feat of features) {
    const geom = feat.geometry;
    if (!geom) continue;
    const rings = geom.type === 'Polygon' ? [geom.coordinates]
                : geom.type === 'MultiPolygon' ? geom.coordinates : [];
    for (const poly of rings) {
      for (const ring of poly) {
        const projected = ring.map(([lon, lat]) => toMm(project(lat, lon)));
        if (projected.length >= 3) polygons.push(projected);
      }
    }
  }
  return polygons;
}

function extractLinesMm(features, project, toMm, classes) {
  const lines = [];
  for (const feat of features) {
    const cls = feat.properties && feat.properties.class;
    if (!classes.includes(cls)) continue;
    const geom = feat.geometry;
    if (!geom) continue;
    const lineArrays = geom.type === 'LineString' ? [geom.coordinates]
                     : geom.type === 'MultiLineString' ? geom.coordinates : [];
    for (const coords of lineArrays) {
      const projected = coords.map(([lon, lat]) => toMm(project(lat, lon)));
      if (projected.length >= 2) lines.push(projected);
    }
  }
  return lines;
}

// Extract features from the VISIBLE MapLibre map and project them
function extractRawFeatures(map, widthKm, onStatus) {
  const center = map.getCenter();
  const lat = center.lat, lon = center.lng;
  const REF_SIZE = 500;
  const mmPerMeter = REF_SIZE / (widthKm * 1000);
  const project = makeProjector(lat, lon);
  const toMm = (pt) => ({ x: pt.x * mmPerMeter, y: pt.y * mmPerMeter });

  if (onStatus) onStatus('Extracting features\u2026');
  const water = map.querySourceFeatures('openmaptiles', { sourceLayer: 'water' });
  const waterway = map.querySourceFeatures('openmaptiles', { sourceLayer: 'waterway' });
  const transportation = map.querySourceFeatures('openmaptiles', { sourceLayer: 'transportation' });
  const places = map.querySourceFeatures('openmaptiles', { sourceLayer: 'place' });
  const waterNames = map.querySourceFeatures('openmaptiles', { sourceLayer: 'water_name' });

  const waterPolygons = extractPolygonsMm(water, project, toMm);
  const waterwayPolygons = extractPolygonsMm(waterway, project, toMm);
  const allWaterRaw = unionPolygons([...waterPolygons, ...waterwayPolygons]);
  const majorRoadsMm = extractLinesMm(transportation, project, toMm, MAJOR_ROAD_CLASSES);
  const minorRoadsMm = extractLinesMm(transportation, project, toMm, MINOR_ROAD_CLASSES);

  const labelsMm = [];
  const seenLabels = new Set();
  const allLabelFeatures = [...places, ...waterNames];
  for (const feat of allLabelFeatures) {
    const name = feat.properties && feat.properties.name;
    const cls = feat.properties && feat.properties.class;
    if (!name || seenLabels.has(name)) continue;
    seenLabels.add(name);
    const geom = feat.geometry;
    if (!geom || geom.type !== 'Point') continue;
    const [lon2, lat2] = geom.coordinates;
    const pt = toMm(project(lat2, lon2));
    const rank = cls === 'city' ? 1 : cls === 'town' ? 2 : cls === 'village' ? 3 : cls === 'suburb' ? 4 : 5;
    labelsMm.push({ name, x: pt.x, y: pt.y, rank, cls });
  }
  labelsMm.sort((a, b) => a.rank - b.rank);

  const margin = 1.5;
  const cacheHalf = (widthKm * margin * 1000 * mmPerMeter) / 2;

  return { lat, lon, mmPerMeter, allWaterRaw, majorRoadsMm, minorRoadsMm, labelsMm, cacheHalfMm: cacheHalf, refSize: REF_SIZE, widthKm };
}

// Phase 2: compute smoothed land + bands from cached raw features
function computeBands(rawCache, onStatus, aspect) {
  aspect = (aspect && isFinite(aspect) && aspect > 0) ? aspect : 1; // heightMm/widthMm of the actual output
  const { allWaterRaw, cacheHalfMm: cacheHalf, refSize: REF_SIZE } = rawCache;
  if (onStatus) onStatus('Smoothing coastlines…');
  const refAspect = 1; // will be applied in buildLayers
  // Use a rect sized to cover the viewport + band offsets, NOT the full cache.
  // This prevents land blocks from appearing at data-edge boundaries.
  // Sized per-axis so tall/wide canvases get bands covering their full extent
  // (a fixed square here was cutting off bands on long canvases).
  const maxBandOffset = (BAND_1_MM + BAND_2_MM + BAND_3_MM) * 2; // generous margin for wide offset
  const viewHalfX = REF_SIZE * 0.75 + maxBandOffset;
  const viewHalfY = REF_SIZE * aspect * 0.75 + maxBandOffset;
  const landComputeRect = [[ { x: -viewHalfX, y: -viewHalfY }, { x: viewHalfX, y: -viewHalfY }, { x: viewHalfX, y: viewHalfY }, { x: -viewHalfX, y: viewHalfY } ]];
  const allWaterClipped = intersectPolygons(allWaterRaw, landComputeRect);
  let fullLand = differencePolygons(landComputeRect, allWaterClipped);

  // Simplify for large areas
  const simplifyTol = Math.max(0.1, REF_SIZE / 2000);
  if (simplifyTol > 0.2) {
    const scaled = fullLand.map(r => r.map(p => ({X: Math.round(p.x * CLIPPER_SCALE), Y: Math.round(p.y * CLIPPER_SCALE)})));
    const cleaned = ClipperLib.Clipper.CleanPolygons(scaled, simplifyTol * CLIPPER_SCALE);
    fullLand = cleaned.map(r => r.map(p => ({x: p.X / CLIPPER_SCALE, y: p.Y / CLIPPER_SCALE}))).filter(r => r.length >= 3);
  }

  let smoothedLandFull = fullLand;
  smoothedLandFull = cleanupPolygons(offsetPolygons(offsetPolygons(smoothedLandFull, SHALLOW_SMOOTH_MM), -SHALLOW_SMOOTH_MM));
  if (REF_SIZE < 400) { // always true at REF_SIZE=500
    smoothedLandFull = cleanupPolygons(offsetPolygons(offsetPolygons(smoothedLandFull, SHALLOW_SMOOTH_MM * 0.6), -SHALLOW_SMOOTH_MM * 0.6));
  }

  if (onStatus) onStatus('Computing depth contours…');
  const mapScale = REF_SIZE;
  const preMaxGap = Math.max(0.8, REF_SIZE / 220);
  const smoothRad = Math.max(4, SHALLOW_SMOOTH_MM * 0.6);
  const tessSegs = 5;

  // Inner boundary: bands must not extend beyond this to avoid data-edge artifacts
  const bandLimitX = viewHalfX, bandLimitY = viewHalfY; // must cover full viewport + offset margin
  const bandClipRect = [[ {x:-bandLimitX,y:-bandLimitY}, {x:bandLimitX,y:-bandLimitY}, {x:bandLimitX,y:bandLimitY}, {x:-bandLimitX,y:bandLimitY} ]];

  function makeBandFull(totalOffset, noiseAmp, seed, noiseScale) {
    let raw = intersectPolygons(offsetPolygons(smoothedLandFull, totalOffset), bandClipRect);
    // Double smoothing pass to round off all angular features
    raw = cleanupPolygons(offsetPolygons(offsetPolygons(raw, smoothRad), -smoothRad));
    raw = cleanupPolygons(offsetPolygons(offsetPolygons(raw, smoothRad * 0.6), -smoothRad * 0.6));
    // Apply different noise to coastal vs enclosed (lake) rings
    const resampled = raw.map(r => resampleRing(r, preMaxGap));
    const displaced = cleanupPolygons(resampled.map(ring => {
      // Check if this ring is near the cache boundary (coastal) or interior (lake)
      const nearEdge = ring.some(p => 
        Math.abs(p.x) > cacheHalf * 0.6 || Math.abs(p.y) > cacheHalf * 0.6
      );
      const amp = nearEdge ? noiseAmp * 1.5 : noiseAmp * 0.25; // lakes get gentler noise
      const sc = nearEdge ? noiseScale : noiseScale * 1.5; // lakes get broader features
      return displaceRingWithNoise(ring, amp, seed, sc);
    }));
    // Double smoothing AFTER noise to eliminate all sharp edges
    let smoothed = cleanupPolygons(offsetPolygons(offsetPolygons(displaced, smoothRad * 0.6), -smoothRad * 0.6));
    smoothed = cleanupPolygons(offsetPolygons(offsetPolygons(smoothed, smoothRad * 0.3), -smoothRad * 0.3));
    // Three-step organic smoothing:
    // 1. Chaikin rounds all angles into smooth curves
    // 2. Low-frequency noise adds flowing artistic deviation
    // 3. Second Chaikin pass smooths any noise edges
    smoothed = chaikinSmoothPolygons(smoothed, 2);
    // Gentle wave deviation (subtle, broad features only)
    const waveAmp = totalOffset * 0.15;
    const waveScale = noiseScale * 3;
    smoothed = smoothed.map(ring => {
      const nearEdge = ring.some(p => Math.abs(p.x) > bandLimitX * 0.7 || Math.abs(p.y) > bandLimitY * 0.7);
      const a = nearEdge ? waveAmp : waveAmp * 0.1;
      return displaceRingWithNoise(ring, a, seed + 5000, waveScale);
    });
    smoothed = cleanupPolygons(smoothed);
    smoothed = chaikinSmoothPolygons(smoothed, 2);
    return smoothed;
  }

  const b1 = BAND_1_MM * bandScale, b2 = BAND_2_MM * bandScale, b3 = BAND_3_MM * bandScale;
  const band1Tess = makeBandFull(b1, b1 * 0.8, 42, mapScale * 0.15);
  const band2Tess = makeBandFull(b1 + b2, b2 * 1.2, 571, mapScale * 0.22);
  const band3Tess = makeBandFull(b1 + b2 + b3, b3 * 1.0, 1847, mapScale * 0.35);

  return { ...rawCache, band1Tess, band2Tess, band3Tess };
}

// Convenience: extract + compute in one call
function extractAndProject(map, widthKm, widthMm, onStatus) {
  const raw = extractRawFeatures(map, widthKm, onStatus);
  return computeBands(raw, onStatus);
}

// Build layers from cached data for a specific viewport
function buildLayers(cache, widthMm, heightMm, panXmm, panYmm, currentRoadMode) {
  const aspect = heightMm / widthMm;
  const halfW = cache.refSize / 2, halfH = (cache.refSize * aspect) / 2;
  const landRect = [[
    { x: panXmm - halfW, y: panYmm - halfH }, { x: panXmm + halfW, y: panYmm - halfH },
    { x: panXmm + halfW, y: panYmm + halfH }, { x: panXmm - halfW, y: panYmm + halfH },
  ]];

  const waterUnion = cleanupPolygons(intersectPolygons(cache.allWaterRaw, landRect));
  const land = differencePolygons(landRect, waterUnion);
  const waterClipped = intersectPolygons(waterUnion, landRect);

  const numBands = layerCount - 2;
  const shallowHoles = numBands >= 1 ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band1Tess, landRect)), 3) : [];
  const midHoles = numBands >= 2 ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band2Tess, landRect)), 3) : [];
  const outerHoles = numBands >= 3 ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band3Tess, landRect)), 3) : [];

  let allRoadPolys = [];
  if (currentRoadMode !== 'off') {
    const majorPolys = intersectPolygons(bufferLinesToPolygons(cache.majorRoadsMm, 0.5 * roadThickMult), landRect);
    allRoadPolys = majorPolys;
    if (currentRoadMode === 'all') {
      const minorPolys = intersectPolygons(bufferLinesToPolygons(cache.minorRoadsMm, 0.22 * roadThickMult), landRect);
      allRoadPolys = unionPolygons([...majorPolys, ...minorPolys]);
    }
  }

  const visibleLabels = cache.labelsMm.filter(l => {
    if (l.x < panXmm - halfW || l.x > panXmm + halfW) return false;
    if (l.y < panYmm - halfH || l.y > panYmm + halfH) return false;
    // Check multiple points around the label to ensure it doesn't hang over water
    const labelW = l.name.length * 1.0 * labelSzMult; // reduced buffer for coastal labels // generous buffer to keep labels well inland // approximate half-width in ref units
    // Also check that the full label text fits within the viewport
    // Remove labels too close to the edge
    if (l.x - labelW < panXmm - halfW + 2 || l.x + labelW > panXmm + halfW - 2) return false;
    if (l.y < panYmm - halfH + 4 || l.y > panYmm + halfH - 4) return false;
    // Only show labels on land
    let crossings = 0;
    for (const ring of waterUnion) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i].x, yi = ring[i].y, xj = ring[j].x, yj = ring[j].y;
        if ((yi > l.y) !== (yj > l.y) && l.x < ((xj - xi) * (l.y - yi)) / (yj - yi) + xi) crossings++;
      }
    }
    return crossings % 2 === 0;
  });

  // Frame: merge into each layer, clip roads to inner area
  const ft = showFrame ? Math.min(halfW*2, halfH*2) * frameThickness : 0;
  let innerClipRect = landRect;
  if (ft > 0) {
    innerClipRect = [[
      { x: panXmm - halfW + ft, y: panYmm - halfH + ft },
      { x: panXmm + halfW - ft, y: panYmm - halfH + ft },
      { x: panXmm + halfW - ft, y: panYmm + halfH - ft },
      { x: panXmm - halfW + ft, y: panYmm + halfH - ft },
    ]];
    // Clip roads to inside the frame
    allRoadPolys = allRoadPolys.length ? intersectPolygons(allRoadPolys, innerClipRect) : [];
  }

  // Compass backer: circle polygon merged with land, subtracted from roads
  let compassBacker = [];
  if (compassCorner !== 'off') {
    const sz = Math.min(halfW*2, halfH*2) * 0.12 * compassSzMult;
    const margin = sz * 0.3;
    const r = sz * 0.56;
    let cx, cy;
    if (compassCorner === 'tl') { cx = panXmm - halfW + margin + sz/2; cy = panYmm - halfH + margin + sz/2; }
    else if (compassCorner === 'tr') { cx = panXmm + halfW - margin - sz/2; cy = panYmm - halfH + margin + sz/2; }
    else if (compassCorner === 'bl') { cx = panXmm - halfW + margin + sz/2; cy = panYmm + halfH - margin - sz/2; }
    else { cx = panXmm + halfW - margin - sz/2; cy = panYmm + halfH - margin - sz/2; }
    // Create circle polygon (32 segments)
    const circPts = [];
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * Math.PI * 2;
      circPts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
    compassBacker = [circPts];
    // Subtract compass area from roads (knock out)
    if (allRoadPolys.length) allRoadPolys = differencePolygons(allRoadPolys, compassBacker);
  }

  // Label backers: compute from the SAME filtered label set used for rendering
  const labelBackerPolys = [];
  const filteredLabels = [];
  if (showLabel && visibleLabels.length) {
    const mapSize = Math.min(halfW * 2, halfH * 2);
    const placed2 = [];
    for (const l of visibleLabels) {
      const sz = (l.rank <= 1 ? mapSize * 0.025 : l.rank <= 2 ? mapSize * 0.018 : l.rank <= 3 ? mapSize * 0.013 : mapSize * 0.01) * labelSzMult;
      if (sz < 1.5) continue;
      const minDist = sz * l.name.length * 0.45;
      const tooClose = placed2.some(p => Math.hypot(p.x - l.x, p.y - l.y) < Math.max(minDist, p.minDist));
      if (tooClose) continue;
      placed2.push({ x: l.x, y: l.y, minDist });
      filteredLabels.push({ ...l, sz });
      const tw = sz * l.name.length * 0.52, pd = sz * 0.35, rh = sz * 1.4;
      const rx = l.x - tw/2 - pd, ry = l.y - rh/2;
      labelBackerPolys.push([{x:rx,y:ry},{x:rx+tw+pd*2,y:ry},{x:rx+tw+pd*2,y:ry+rh},{x:rx,y:ry+rh}]);
    }
    if (allRoadPolys.length && labelBackerPolys.length) {
      allRoadPolys = differencePolygons(allRoadPolys, labelBackerPolys);
    }
  }

  // Don't merge backers into land here (preview renders them as separate SVG elements).
  // Backers are merged into land only during export for laser-cutter compatibility.

  return { shallowHoles, midHoles, outerHoles, land, allRoadPolys, visibleLabels, filteredLabels,
           panXmm, panYmm, halfW, halfH, compassBacker, labelBackerPolys, ft, innerClipRect };
}

/* =========================================================================
   SVG EXPORT
   ========================================================================= */

function polygonsToPathD(polygons) {
  return polygons.map((ring) => {
    const pts = ring.filter(p => isFinite(p.x) && isFinite(p.y));
    if (pts.length < 3) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ') + ' Z';
  }).filter(Boolean).join(' ');
}

function rectPathD(w, h, px, py) {
  const hW = w/2, hH = h/2;
  return `M ${px-hW},${py-hH} L ${px+hW},${py-hH} L ${px+hW},${py+hH} L ${px-hW},${py+hH} Z`;
}

function bandPathD(holes, w, h, px, py) {
  const rect = rectPathD(w, h, px, py);
  const holePaths = polygonsToPathD(holes);
  return holePaths ? `${rect} ${holePaths}` : rect;
}

// Frame: outer rect with inner rect hole, creating a border
function frameSvg(w, h, px, py, thickness, color) {
  if (!showFrame || !thickness) return '';
  const hW = w/2, hH = h/2, t = thickness;
  const outer = `M ${px-hW},${py-hH} L ${px+hW},${py-hH} L ${px+hW},${py+hH} L ${px-hW},${py+hH} Z`;
  const inner = `M ${px-hW+t},${py-hH+t} L ${px+hW-t},${py-hH+t} L ${px+hW-t},${py+hH-t} L ${px-hW+t},${py+hH-t} Z`;
  return `<path d="${outer} ${inner}" fill="${color || '#c8c0ab'}" fill-rule="evenodd"/>`;
}

function innerRect(w, h, px, py, thickness) {
  const hW = w/2, hH = h/2, t = showFrame ? thickness : 0;
  return { x1: px-hW+t, y1: py-hH+t, x2: px+hW-t, y2: py+hH-t };
}

// Compass rose
const COMPASS_SVG = `<path d="M338.659,123.74c-27.17-27.021-60.29-41.128-98.528-43.504c-2.452-10.842-4.85-21.448-7.249-32.055
 c-0.222,0.028-0.445,0.055-0.667,0.083c-2.374,10.681-4.748,21.361-7.127,32.064c-38.043,2.742-70.697,16.983-97.495,43.817
 c-26.859,26.895-41.102,59.644-43.703,97.641c-10.902,2.389-21.623,4.738-32.345,7.087c0.026,0.239,0.052,0.478,0.077,0.717
 c10.782,2.387,21.565,4.773,32.233,7.134c2.466,38.474,16.666,71.355,43.564,98.372c26.957,27.076,59.87,41.421,98.096,43.993
 c2.396,10.84,4.764,21.554,7.132,32.268c0.188,0.024,0.376,0.048,0.564,0.072c2.383-10.761,4.767-21.523,7.157-32.314
 c38.448-2.378,71.498-16.666,98.593-43.748c27.153-27.139,41.494-60.269,43.799-98.676c10.996-2.42,21.712-4.777,32.428-7.135
 c0.011-0.218,0.022-0.436,0.033-0.654c-10.784-2.336-21.569-4.672-32.504-7.041C380.166,183.571,365.753,150.685,338.659,123.74z
  M223.015,88.637c-0.02,2.135-0.571,3.957-1.202,5.999c-32.839,3.17-61.167,16.134-84.431,39.463
 c-23.208,23.271-36.051,51.563-39.221,84.334c-1.95,0.697-3.842,1.255-5.888,1.205C94.837,153.072,152.639,92.23,223.015,88.637z
  M150.727,146.175c-0.206,0.173-0.412,0.346-0.618,0.519c11.285,17.555,22.57,35.111,34.171,53.159
 c-24.041,5.25-47.646,10.404-71.883,15.696c3.448-26.513,13.79-49.235,31.624-68.412c20.135-21.651,44.975-34.29,74.814-38.259
 c-5.29,23.976-10.469,47.446-15.753,71.394C185.345,168.721,168.036,157.448,150.727,146.175z M202.699,182.93
 c-0.803,4.392-1.587,8.678-2.483,13.576c-13.799-13.556-27.135-26.658-40.471-39.759c0.127-0.168,0.254-0.336,0.381-0.504
 C174.242,165.091,188.359,173.94,202.699,182.93z M75.205,229.307c-0.997,0-1.993,0-2.99,0c-0.035-0.265-0.07-0.53-0.104-0.796
 c43.394-8.602,86.788-17.205,130.212-25.813c6.598,5.77,13.066,11.428,19.535,17.084c2.855,2.497,5.714,4.99,8.568,7.488
 c0.737,0.645,1.561,1.244,1.46,2.381c-1.198-0.636-2.497-0.325-3.744-0.325C177.163,229.305,126.184,229.307,75.205,229.307z
  M150.446,313.388c17.521-11.574,35.042-23.148,53.011-35.018c5.31,24.208,10.52,47.964,15.748,71.799
 c-50.462-3.844-101.011-48.047-106.656-107.186c24.109,5.292,47.89,10.511,72.185,15.844
 c-11.752,18.288-23.255,36.187-34.757,54.087C150.134,313.072,150.29,313.23,150.446,313.388z M159.335,301.804
 c8.69-13.535,17.862-27.819,27.148-42.283c4.105,1.182,8.069,2.323,11.847,3.411C185.111,276.109,171.94,289.239,159.335,301.804z
  M133.835,330.601c-29.246-28.459-41.3-66.425-41.624-91.863c1.943,0.023,3.762,0.571,5.826,1.146
 c3.008,33.024,15.766,61.703,39.248,85.294c23.409,23.519,51.894,36.473,84.782,39.638c1.031,2.397,1.274,3.552,1.277,6.314
 C188.619,368.267,158.769,354.864,133.835,330.601z M232.664,390.964c0,0.524-0.094,1.047-0.561,1.617
 c-9.622-45.424-19.243-90.849-28.907-136.471c3.029-2.763,6.015-5.506,9.023-8.227c5.831-5.275,11.685-10.527,17.501-15.819
 c0.776-0.706,1.778-1.23,2.187-2.291c1.162,0.508,0.747,1.565,0.747,2.405C232.672,285.106,232.671,338.035,232.664,390.964z
  M326.131,122.696c29.266,25.597,45.29,58.025,48.646,96.792c-2.425,0.259-3.141,0.14-6.337-1.007
 c-3.056-32.989-16.14-61.536-39.774-84.952c-23.56-23.342-52.16-36.117-85.148-39.023c-0.804-2-1.177-3.858-1.373-6.246
 C274.11,90.495,302.192,101.758,326.131,122.696z M316.28,146.614c-0.142-0.184-0.284-0.368-0.426-0.552
 c-17.656,11.246-35.312,22.491-53.432,34.032c-5.346-23.914-10.59-47.37-15.947-71.334c27.217,3.187,50.505,13.817,70.09,32.277
 c21.371,20.143,33.82,44.903,37.702,74.667c-24.204-5.22-47.901-10.331-72.178-15.567
 C293.708,181.948,304.994,164.281,316.28,146.614z M279.269,199.894c-4.497-1.218-8.742-2.367-12.764-3.456
 c13.301-13.293,26.523-26.508,39.169-39.147C297.264,170.86,288.362,185.223,279.269,199.894z M233.324,66.818
 c0.111-0.023,0.222-0.046,0.333-0.069c9.894,45.353,19.787,90.705,29.704,136.164c-9.981,8.64-19.82,17.156-30.038,26
 C233.324,174.571,233.324,120.695,233.324,66.818z M344.815,317.096c-26.078,32.77-60.332,50.502-102.474,54.186
 c0.137-1.296,0.159-2.03,0.303-2.739c0.237-1.163,0.553-2.31,0.876-3.628c33.296-3.014,62.022-15.889,85.617-39.518
 c23.531-23.566,36.454-52.18,39.368-85.19c0.953-0.831,1.992-0.902,2.971-1.115c0.895-0.195,1.804-0.328,3.296-0.592
 C372.856,267.994,363.007,294.236,344.815,317.096z M262.428,278.164c18.04,11.873,35.682,23.483,53.323,35.093
 c0.178-0.15,0.356-0.299,0.534-0.449c-11.346-17.856-22.692-35.711-34.379-54.103c24.328-5.317,48.108-10.515,72.435-15.832
 c-3.507,28.869-15.194,53.129-35.348,73.162c-19.879,19.76-43.926,31.11-72.436,34.674
 C251.895,326.312,257.11,302.471,262.428,278.164z M262.383,275.129c1.098-4.556,2.09-8.668,3.06-12.694
 c13.418,13.705,26.798,27.372,37.625,38.432C291.714,293.684,277.121,284.453,262.383,275.129z M262.468,254.569
 c-9.438-8.191-18.899-16.402-28.524-24.755c2.887-0.751,158.569-0.725,160.993-0.071
 C392.556,230.445,265.89,254.215,262.468,254.569z"/> <path d="M45.572,212.585c-1.732,7.026-3.442,13.962-5.152,20.898c-0.221-0.004-0.442-0.009-0.664-0.013
 c-1.655-6.954-3.31-13.908-4.963-20.859c-1.933-0.276-3.553-0.385-5.393,0.074c-1.705,6.962-3.414,13.935-5.122,20.908
 c-0.174,0.009-0.348,0.019-0.522,0.028c-1.82-7.018-3.64-14.036-5.514-21.259c-1.715,0.182-3.325-0.38-5.075,0.446
 c2.199,8.72,4.358,17.281,6.523,25.866c2.632,0.247,4.985,0.387,7.493-0.111c1.495-5.474,2.973-10.882,4.45-16.289
 c0.19,0.019,0.381,0.038,0.571,0.056c1.471,5.485,2.942,10.97,4.405,16.425c2.552,0.126,4.744,0.304,7.143-0.137
 c2.148-8.637,4.295-17.267,6.454-25.945C48.486,212.221,47.22,212.319,45.572,212.585z"/> <path d="M225.389,37.909c0-6.944,0-13.947,0-20.951c0.17-0.021,0.34-0.042,0.51-0.063c4.007,7.146,8.014,14.293,12.051,21.492
 c1.949-0.144,3.681,0.3,5.439-0.343c0-9.247,0-18.414,0-27.62c-1.678-0.164-3.116-0.448-4.762,0.306c0,6.137,0,12.264,0,18.392
 c-0.188,0.055-0.375,0.109-0.563,0.164c-3.632-6.352-7.264-12.703-10.843-18.961c-2.329,0-4.352,0-6.643,0
 c0,9.304,0,18.534,0,27.955C222.177,38.323,223.611,38.624,225.389,37.909z"/> <path d="M241.509,436.792c-2.469-1.847-5.379-2.839-8.151-4.097c-1.681-0.763-3.404-1.475-4.706-2.856
 c-1.84-1.951-1.374-4.333,1.034-5.393c4.07-1.792,7.919-0.299,9.604,3.785c1.507,0.173,3.042,0.397,4.633-0.202
 c0.316-2.636-1.036-4.356-3.176-5.287c-5.065-2.204-10.312-2.275-15.339-0.038c-4.13,1.838-4.466,6.377-0.924,9.184
 c2.199,1.742,4.836,2.675,7.353,3.826c1.929,0.882,3.965,1.603,5.529,3.107c1.842,1.772,1.585,4.145-0.512,5.344
 c-4.334,2.478-9.149,0.46-10.182-4.417c-1.398,0-2.836,0-4.467,0c-0.435,3.42,0.401,5.336,3.196,6.465
 c4.993,2.016,10.128,2.128,15.1,0.004C244.977,444.304,245.393,439.699,241.509,436.792z"/> <path d="M444.156,236.697c-4.439,0-9.19,0-14.116,0c-0.061-3.467-0.344-6.549,0.238-9.914c3.97,0,7.721,0,11.331,0
 c0.447-1.02,0.5-1.717-0.139-2.77c-3.606,0-7.429,0-11.367,0c0-3.289,0-6.207,0-9.604c5.067,0,9.747,0,14.216,0
 c0.653-0.995,0.639-1.684,0.186-2.526c-6.534,0-13.131,0-19.799,0c0,9.297,0,18.386,0,27.607c6.751,0,13.277,0,19.873,0
 C444.827,238.59,445.149,237.825,444.156,236.697z"/>`;

function compassSvg(w, h, px, py, skipBacker) {
  if (compassCorner === 'off') return '';
  const sz = Math.min(w, h) * 0.12 * compassSzMult;
  const scale = sz / 458;
  const margin = sz * 0.3;
  const hW = w/2, hH = h/2;
  let cx, cy;
  if (compassCorner === 'tl') { cx = px - hW + margin + sz/2; cy = py - hH + margin + sz/2; }
  else if (compassCorner === 'tr') { cx = px + hW - margin - sz/2; cy = py - hH + margin + sz/2; }
  else if (compassCorner === 'bl') { cx = px - hW + margin + sz/2; cy = py + hH - margin - sz/2; }
  else { cx = px + hW - margin - sz/2; cy = py + hH - margin - sz/2; }
  const tx = cx - sz/2, ty = cy - sz/2;
  const backer = skipBacker ? '' : `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(sz*0.56).toFixed(1)}" fill="#d8d2c0"/>`;
  return backer + `<g transform="translate(${tx.toFixed(1)},${ty.toFixed(1)}) scale(${scale.toFixed(6)})" fill="#594a42">${COMPASS_SVG}</g>`;
}

// Title + subtitle
function titleSvg(w, h, px, py) {
  const title = document.getElementById('title-input').value.trim();
  const subtitle = document.getElementById('subtitle-input').value.trim();
  if (!title && !subtitle) return '';
  const hW = w/2, hH = h/2;
  const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
  const subSz = Math.min(w, h) * 0.025 * subSzMult;
  const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
  const totalTextH = (title ? titleSz * lineGap : 0) + (subtitle ? subSz * lineGap : 0);
  let baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
  let baseX = px - hW + titleSz * 0.5 + offsetX;
  let subBaseX = baseX + w * subDx / 100;
  let out = '';
  const strokeW = (titleSz * 0.35).toFixed(1);
  const strokeAttr = ' stroke="#d8d2c0" stroke-width="'+strokeW+'" stroke-linejoin="round" paint-order="stroke"';

  // Rect backer mode - measure actual text widths
  if (titleBackerStyle === 'rect') {
    const pad = titleSz * 0.3 * rectPadMult;
    const titleW = title ? measureTextWidth(title, titleFont, titleSz, '800') : 0;
    const subW = subtitle ? measureTextWidth(subtitle, 'Inter,Helvetica,sans-serif', subSz, '600') : 0;
    const hPadExtra = titleSz * 0.15; // extra horizontal padding
    const leftEdge = Math.min(baseX, subBaseX) - pad - hPadExtra;
    const rightEdge = Math.max(baseX + titleW, subBaseX + subW) + pad + hPadExtra;
    out += '<rect x="'+leftEdge.toFixed(1)+'" y="'+(baseY - pad).toFixed(1)+'" width="'+(rightEdge - leftEdge).toFixed(1)+'" height="'+(totalTextH + pad * 2).toFixed(1)+'" rx="'+(titleSz * 0.15).toFixed(1)+'" fill="#d8d2c0"/>\n';
  }

  // Unified offset: render ALL text with stroke first (background layer)
  if (titleOffsetOn) {
    if (title) {
      const y = baseY + titleSz * 0.9;
      out += '<text x="'+baseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="'+titleFont+'" font-size="'+titleSz.toFixed(1)+'" font-weight="800" fill="#d8d2c0" letter-spacing="0"'+strokeAttr+'>'+title.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
    }
    if (subtitle) {
      const y = baseY + (title ? titleSz * lineGap : 0) + subSz * 0.9;
      out += '<text x="'+subBaseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="Inter,Helvetica,sans-serif" font-size="'+subSz.toFixed(1)+'" font-weight="600" fill="#d8d2c0" letter-spacing="0"'+strokeAttr+'>'+subtitle.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
    }
  }

  // Foreground text (on top of offset)
  if (title) {
    const y = baseY + titleSz * 0.9;
    out += '<text x="'+baseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="'+titleFont+'" font-size="'+titleSz.toFixed(1)+'" font-weight="800" fill="#594a42" letter-spacing="0">'+title.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
  }
  if (subtitle) {
    const y = baseY + (title ? titleSz * lineGap : 0) + subSz * 0.9;
    out += '<text x="'+subBaseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="Inter,Helvetica,sans-serif" font-size="'+subSz.toFixed(1)+'" font-weight="600" fill="#594a42" letter-spacing="0">'+subtitle.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
  }
  return out;
}

function labelSvg(layers, skipBackers) {
  if (!showLabel || !layers.visibleLabels || !layers.visibleLabels.length) return '';
  const mapSize = Math.min(layers.halfW * 2, layers.halfH * 2);
  const placed = [];
  const filtered = [];
  for (const l of layers.visibleLabels) {
    const sz = (l.rank <= 1 ? mapSize * 0.025 : l.rank <= 2 ? mapSize * 0.018 : l.rank <= 3 ? mapSize * 0.013 : mapSize * 0.01) * labelSzMult;
    if (sz < 1.5) continue;
    const minDist = sz * l.name.length * 0.45;
    const tooClose = placed.some(p => Math.hypot(p.x - l.x, p.y - l.y) < Math.max(minDist, p.minDist));
    if (!tooClose) { placed.push({ x: l.x, y: l.y, minDist }); filtered.push({ ...l, sz }); }
  }
  let out = '\n';
  for (const l of filtered) {
    const esc = l.name.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    if (!skipBackers) {
      const tw = l.sz * l.name.length * 0.52, pad = l.sz * 0.35, rh = l.sz * 1.4;
      out += '    <rect x="' + (l.x-tw/2-pad).toFixed(1) + '" y="' + (l.y-rh/2).toFixed(1) + '" width="' + (tw+pad*2).toFixed(1) + '" height="' + rh.toFixed(1) + '" rx="' + (l.sz*0.2).toFixed(1) + '" fill="#d8d2c0"/>\n';
    }
    out += '    <text x="' + l.x.toFixed(1) + '" y="' + l.y.toFixed(1) + '" font-family="Inter,Helvetica,Arial,sans-serif" font-size="' + l.sz.toFixed(2) + '" font-weight="' + (l.rank<=2?'700':'600') + '" fill="#594a42" text-anchor="middle" dominant-baseline="central">' + esc + '</text>\n';
  }
  return out;
}

function svgWrap(w, h, px, py, inner, outW, outH) {
  const hW = w/2, hH = h/2;
  const ow = outW || w, oh = outH || h;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${px-hW} ${py-hH} ${w} ${h}" width="${ow}mm" height="${oh}mm">\n${inner}\n</svg>`;
}

function buildFullSvg(layers, w, h, px, py) {
  const hW = w/2, hH = h/2;
  const roads = layers.allRoadPolys.length ? '\n    <path d="' + polygonsToPathD(layers.allRoadPolys) + '" fill="#594a42"/>' : '';
  const frameT = Math.min(w, h) * frameThickness;
  return svgWrap(w, h, px, py, `    <rect x="${px-hW}" y="${py-hH}" width="${w}" height="${h}" fill="#00556b"/>
${layers.outerHoles.length ? '    <path d="' + bandPathD(layers.outerHoles, w, h, px, py) + '" fill="#00778c" fill-rule="evenodd"/>' : ''}
${layers.midHoles.length ? '    <path d="' + bandPathD(layers.midHoles, w, h, px, py) + '" fill="#239fb8" fill-rule="evenodd"/>' : ''}
${layers.shallowHoles.length ? '    <path d="' + bandPathD(layers.shallowHoles, w, h, px, py) + '" fill="#3ec5d9" fill-rule="evenodd"/>' : ''}
    <path d="${polygonsToPathD(layers.land)}" fill="#d8d2c0" fill-rule="evenodd"/>${roads}${labelSvg(layers)}
    ${frameSvg(w, h, px, py, frameT, "#d8d2c0")}
    ${compassSvg(w, h, px, py)}
    ${titleSvg(w, h, px, py)}`);
}

function buildLayeredExportSvg(layers, w, h, px, py, outW, outH, otTitleFont, otInterFont) {
  const gap = Math.max(14, Math.round(Math.max(w, h) * 0.06));
  const fontSize2 = Math.max(3.5, +(Math.max(w, h) * 0.016).toFixed(2));
  const labelGap = fontSize2 * 2.2;
  const hW = w/2, hH = h/2;
  const pad = fontSize2 * 1.5;
  let exportRoads = layers.allRoadPolys;
  // Roads will be knocked out under title offset and compass backer in the final panel build
  const roads = ''; // placeholder, actual roads rendered per-panel below

  // Build land layer with ALL merges: frame + compass backer + label backers + title offset
  let exportLand = layers.land.slice();

  // Merge frame
  if (layers.ft > 0) {
    const frameShape = differencePolygons(
      [[{x:px-hW,y:py-hH},{x:px+hW,y:py-hH},{x:px+hW,y:py+hH},{x:px-hW,y:py+hH}]],
      [[{x:px-hW+layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py+hH-layers.ft},{x:px-hW+layers.ft,y:py+hH-layers.ft}]]
    );
    exportLand = unionPolygons([...exportLand, ...frameShape]);
  }

  // Merge compass backer into land for export
  if (layers.compassBacker && layers.compassBacker.length) {
    try {
      // Use cleanupPolygons to normalize winding direction before union
      const norm = cleanupPolygons(layers.compassBacker);
      if (norm.length) {
        const merged = unionPolygons([...exportLand, ...norm]);
        if (merged.length) exportLand = merged;
      }
    } catch(e) { console.warn('Compass merge failed:', e); }
  }

  // Merge label backers into land for export  
  if (layers.labelBackerPolys && layers.labelBackerPolys.length) {
    try {
      const norm = cleanupPolygons(layers.labelBackerPolys);
      if (norm.length) {
        const merged = unionPolygons([...exportLand, ...norm]);
        if (merged.length) exportLand = merged;
      }
    } catch(e) { console.warn('Label backer merge failed:', e); }
  }

  let titleKnockout = [];

  const titleText = document.getElementById('title-input').value.trim();
  const subtitleText = document.getElementById('subtitle-input').value.trim();

  // Rectangle backer: merge rounded rect into land (if rect mode)
  if (titleBackerStyle === 'rect' && (titleText || subtitleText)) {
    const _tSz = Math.min(w, h) * 0.045 * titleSzMult;
    const _sSz = Math.min(w, h) * 0.025 * subSzMult;
    const _offX = w * titleDx / 100, _offY = h * titleDy / 100;
    const _totH = (titleText ? _tSz * lineGap : 0) + (subtitleText ? _sSz * lineGap : 0);
    const _baseY = py + hH - _totH - _tSz * 0.4 + _offY;
    const _baseX = px - hW + _tSz * 0.5 + _offX;
    const _subBX = _baseX + w * subDx / 100;
    const pad = _tSz * 0.3 * rectPadMult;
    const hPadExtra = _tSz * 0.15;
    const titleW = titleText ? measureTextWidth(titleText, titleFont, _tSz, '800') : 0;
    const subW = subtitleText ? measureTextWidth(subtitleText, 'Inter,Helvetica,sans-serif', _sSz, '600') : 0;
    const rx = Math.min(_baseX, _subBX) - pad - hPadExtra;
    const ry = _baseY - pad;
    const rw = Math.max(_baseX + titleW, _subBX + subW) + pad + hPadExtra - rx;
    const rh = _totH + pad * 2;
    // Create rectangle and round corners via ClipperLib offset (shrink then expand)
    const cr = _tSz * 0.15;
    const rectPoly = [{x:rx,y:ry}, {x:rx+rw,y:ry}, {x:rx+rw,y:ry+rh}, {x:rx,y:ry+rh}];
    // Shrink by corner radius then expand back = rounded corners
    const shrunk = offsetPolygons([rectPoly], -cr);
    const normRect = shrunk.length ? offsetPolygons(shrunk, cr) : cleanupPolygons([rectPoly]);
    if (normRect.length) {
      exportLand = unionPolygons([...exportLand, ...normRect]);
      titleKnockout.push(...normRect);
    }
  }

  // Title offset: expand text outlines and merge into land
  if (titleOffsetOn && (titleText || subtitleText)) {
    const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
    const subSz2 = Math.min(w, h) * 0.025 * subSzMult;
    const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
    const totalTextH = (titleText ? titleSz * lineGap : 0) + (subtitleText ? subSz2 * lineGap : 0);
    const baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
    const baseX = px - hW + titleSz * 0.5 + offsetX;
    const subBaseX2 = baseX + w * subDx / 100;
    const expandDist = titleSz * 0.18;

    // titleKnockout already declared above
    if (titleText && otTitleFont) {
      const y = baseY + titleSz * 0.9;
      const textPolys = textToPolygons(titleText, otTitleFont, titleSz, baseX, y);
      if (textPolys.length) {
        const expanded = offsetPolygons(textPolys, expandDist);
        if (expanded.length) {
          exportLand = unionPolygons([...exportLand, ...expanded]);
          titleKnockout.push(...expanded);
        }
      }
    }
    if (subtitleText && otInterFont) {
      const y = baseY + (titleText ? titleSz * lineGap : 0) + subSz2 * 0.9;
      const textPolys = textToPolygons(subtitleText, otInterFont, subSz2, subBaseX2, y);
      if (textPolys.length) {
        const expanded = offsetPolygons(textPolys, expandDist);
        if (expanded.length) {
          exportLand = unionPolygons([...exportLand, ...expanded]);
          titleKnockout.push(...expanded);
        }
      }
    }
  }

  // Convert title/subtitle text to paths (instead of <text> elements)
  let titlePaths = '';
  if (titleText || subtitleText) {
    const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
    const subSz2 = Math.min(w, h) * 0.025 * subSzMult;
    const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
    const totalTextH = (titleText ? titleSz * lineGap : 0) + (subtitleText ? subSz2 * lineGap : 0);
    const baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
    const baseX = px - hW + titleSz * 0.5 + offsetX;
    const subBaseX2 = baseX + w * subDx / 100;

    if (titleText && otTitleFont) {
      const y = baseY + titleSz * 0.9;
      const polys = textToPolygons(titleText, otTitleFont, titleSz, baseX, y);
      if (polys.length) titlePaths += '<path d="' + polygonsToPathD(polys) + '" fill="#594a42" fill-rule="evenodd"/>';
    }
    if (subtitleText && otInterFont) {
      const y = baseY + (titleText ? titleSz * lineGap : 0) + subSz2 * 0.9;
      const polys = textToPolygons(subtitleText, otInterFont, subSz2, subBaseX2, y);
      if (polys.length) titlePaths += '<path d="' + polygonsToPathD(polys) + '" fill="#594a42" fill-rule="evenodd"/>';
    }
  }

  // Compute title area for label knockout
  let titleBoundsRect = null;
  if (titleText || subtitleText) {
    const _titleSz = Math.min(w, h) * 0.045 * titleSzMult;
    const _subSz = Math.min(w, h) * 0.025 * subSzMult;
    const _offsetX = w * titleDx / 100, _offsetY = h * titleDy / 100;
    const _totalH = (titleText ? _titleSz * lineGap : 0) + (subtitleText ? _subSz * lineGap : 0);
    const _baseY = py + hH - _totalH - _titleSz * 0.4 + _offsetY;
    const _baseX = px - hW + _titleSz * 0.5 + _offsetX;
    const _subBaseX = _baseX + w * subDx / 100;
    const _titleW = titleText && otTitleFont ? otTitleFont.getAdvanceWidth(titleText, _titleSz) : 0;
    const _subW = subtitleText && otInterFont ? otInterFont.getAdvanceWidth(subtitleText, _subSz) : 0;
    const _expandD = _titleSz * 0.18;
    const _pad = _expandD * 2;
    titleBoundsRect = {
      x1: Math.min(_baseX, _subBaseX) - _pad,
      y1: _baseY - _pad,
      x2: Math.max(_baseX + _titleW, _subBaseX + _subW) + _pad,
      y2: _baseY + _totalH + _pad
    };
  }

  // Convert place labels to paths using opentype.js
  let labelPaths = '';
  if (showLabel && layers.filteredLabels && layers.filteredLabels.length && otInterFont) {
    for (const l of layers.filteredLabels) {
      const sz = l.sz;
      // Skip labels that overlap the title area
      if (titleBoundsRect && l.x > titleBoundsRect.x1 && l.x < titleBoundsRect.x2 &&
          l.y > titleBoundsRect.y1 && l.y < titleBoundsRect.y2) continue;
      const textWidth = otInterFont.getAdvanceWidth(l.name, sz);
      const tx = l.x - textWidth / 2;
      const ty = l.y + sz * 0.35;
      const polys = textToPolygons(l.name, otInterFont, sz, tx, ty);
      if (polys.length) {
        // Slightly expand paths to simulate bold weight (variable font defaults to Regular)
        const boldened = offsetPolygons(polys, sz * 0.03);
        const finalPolys = boldened.length ? boldened : polys;
        labelPaths += '<path d="' + polygonsToPathD(finalPolys) + '" fill="#594a42" fill-rule="evenodd"/>';
      }
    }
  }

  // Compass SVG (already paths, just render as-is)
  const compass = compassSvg(w, h, px, py, true); // skip backer - already merged into land

  const panels = [{ inner: '<rect x="'+(px-hW)+'" y="'+(py-hH)+'" width="'+w+'" height="'+h+'" fill="#00556b"/>', label: 'DEEP WATER — BASE' }];

  if (layers.outerHoles.length) {
    let bandHoles = layers.outerHoles;
    if (layers.ft > 0) {
      const innerR = [[{x:px-hW+layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py+hH-layers.ft},{x:px-hW+layers.ft,y:py+hH-layers.ft}]];
      bandHoles = intersectPolygons(bandHoles, innerR);
    }
    panels.push({ inner: '<path d="' + bandPathD(bandHoles, w, h, px, py) + '" fill="#00778c" fill-rule="evenodd"/>', label: 'OUTER WATER — CUT' });
  }
  if (layers.midHoles.length) {
    let bandHoles = layers.midHoles;
    if (layers.ft > 0) {
      const innerR = [[{x:px-hW+layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py+hH-layers.ft},{x:px-hW+layers.ft,y:py+hH-layers.ft}]];
      bandHoles = intersectPolygons(bandHoles, innerR);
    }
    panels.push({ inner: '<path d="' + bandPathD(bandHoles, w, h, px, py) + '" fill="#239fb8" fill-rule="evenodd"/>', label: 'MID WATER — CUT' });
  }
  if (layers.shallowHoles.length) {
    let bandHoles = layers.shallowHoles;
    if (layers.ft > 0) {
      const innerR = [[{x:px-hW+layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py+hH-layers.ft},{x:px-hW+layers.ft,y:py+hH-layers.ft}]];
      bandHoles = intersectPolygons(bandHoles, innerR);
    }
    panels.push({ inner: '<path d="' + bandPathD(bandHoles, w, h, px, py) + '" fill="#3ec5d9" fill-rule="evenodd"/>', label: 'SHALLOW WATER — CUT' });
  }

  // Fallback: if no text paths were generated, use <text> elements
  if (!titlePaths && (titleText || subtitleText)) {
    titlePaths = titleSvg(w, h, px, py);
  }
  // Labels: no <text> fallback - must be paths for laser cutting

  // Land panel: merged land path + roads + compass + text paths
  // Knock out title offset and compass from roads
  if (titleKnockout && titleKnockout.length && exportRoads.length) {
    try { exportRoads = differencePolygons(exportRoads, titleKnockout); } catch(e) {}
  }
  if (layers.compassBacker && layers.compassBacker.length && exportRoads.length) {
    try { exportRoads = differencePolygons(exportRoads, layers.compassBacker); } catch(e) {}
  }
  const roadsPath = exportRoads.length ? '<path d="' + polygonsToPathD(exportRoads) + '" fill="#594a42"/>' : '';
  panels.push({ inner: '<path d="' + polygonsToPathD(exportLand) + '" fill="#d8d2c0" fill-rule="evenodd"/>' + roadsPath + labelPaths + compass + titlePaths, label: 'LAND — CUT + ENGRAVE' });

  const step = w + gap;
  const totalW = panels.length * w + (panels.length - 1) * gap + pad * 2;
  const totalH = h + labelGap + fontSize2 + pad * 2;
  const scaleRatio = (outW || w) / w;
  const body = panels.map((panel, i) => {
    const cx = pad + i * step + hW;
    return '  <g transform="translate(' + cx + ',' + (pad + hH) + ')">' + panel.inner + '</g>\n  <text x="' + cx + '" y="' + (pad + h + labelGap) + '" text-anchor="middle" font-family="Inter,system-ui,sans-serif" font-size="' + fontSize2 + '" fill="#333">' + panel.label + '</text>';
  }).join('\n');
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalW + ' ' + totalH + '" width="' + (totalW * scaleRatio).toFixed(1) + 'mm" height="' + (totalH * scaleRatio).toFixed(1) + 'mm">\n' + body + '\n</svg>';
}

/* =========================================================================
   VISIBLE MAPLIBRE MAP
   ========================================================================= */

const mapContainer = document.getElementById('map-container');
const svgView = document.getElementById('svg-view');
const svgContainer = document.getElementById('map-svg-container');
const backToMapBtn = document.getElementById('back-to-map');

// Set initial preview box size
(function() {
  var f = document.getElementById('board-frame');
  var w = f ? f.clientWidth || 600 : 600;
  var box = document.getElementById('preview-box');
  if (box) {
    var aspect0 = 1; // 10x10 default = square
    var h0 = Math.round(w * aspect0);
    if (h0 > 850) { h0 = 850; w = Math.round(850 / aspect0); box.style.width = w + 'px'; }
    box.style.height = Math.max(300, h0) + 'px';
  }
})();

let glMap;
try {
  glMap = new maplibregl.Map({
    container: 'map-container',
    style: 'https://tiles.openfreemap.org/styles/liberty',
    center: [-97.14, 49.89],
    zoom: 10,
    attributionControl: false,
  });
  glMap.addControl(new maplibregl.NavigationControl(), 'top-right');
} catch (e) {
  document.getElementById('map-container').innerHTML = '<div style="padding:40px;text-align:center;color:#888;font-size:14px;">Download this file and open in Chrome/Firefox/Safari.</div>';
}

function showMap() {
  mapContainer.style.display = 'block';
  svgView.style.display = 'none';
  document.querySelectorAll('.hide-in-svg').forEach(el => el.style.display = '');
}
function showSvg() {
  mapContainer.style.display = 'none';
  svgView.style.display = 'flex';
  document.querySelectorAll('.hide-in-svg').forEach(el => el.style.display = 'none');
}
backToMapBtn.addEventListener('click', showMap);

function mapWidthKm() {
  if (!glMap) return 10;
  const bounds = glMap.getBounds();
  const lat = glMap.getCenter().lat;
  return (bounds.getEast() - bounds.getWest()) * 111.320 * Math.cos(lat * Math.PI / 180);
}

/* =========================================================================
   UI WIRING
   ========================================================================= */

const placeInput = document.getElementById('place-input');
const generateBtn = document.getElementById('generate-btn');
const statusEl = document.getElementById('status');
const engraveSeg = document.getElementById('engrave-seg');
const unitSeg = document.getElementById('unit-seg');
const sizeHint = document.getElementById('size-hint');
const dlBtn = document.getElementById('dl-full');

let roadMode = 'all', roadThickMult = 1.0, bandScale = 1.0, useInches = true;
let showLabel = true, showFrame = true, compassSzMult = 1.0, labelSzMult = 1.0, frameThickness = 0.02, compassCorner = 'tl', layerCount = 5;
let titleFont = "'Great Vibes',cursive", titleAlign = 'left', titleOffsetOn = true, titleBackerStyle = 'offset' /* always on */, titleDx = 0, titleDy = 0;
let titleSzMult = 1.0, subSzMult = 1.0, subDx = 0, lineGap = 1.3, rectPadMult = 1.0;
let dimW = DEFAULT_W_IN, dimH = DEFAULT_H_IN;
let lastResult = null, featureCache = null, rawFeatureCache = null, lastWidthKm = null;
const bandCache = {}; // cache computed bands per bandScale value

function toMmDim(v) { return useInches ? v * INCHES_TO_MM : v; }
function dimUnit() { return useInches ? 'in' : 'mm'; }
function dimMin() { return useInches ? MIN_DIM_IN : MIN_DIM_MM; }
function dimMax() { return useInches ? MAX_DIM_IN : MAX_DIM_MM; }
function dimStep() { return useInches ? STEP_IN : STEP_MM; }
function setStatus(msg, type) { statusEl.textContent = msg || ''; statusEl.className = 'status' + (type ? ' ' + type : ''); }

function updateMapAspect() {
  const aspect = toMmDim(dimH) / toMmDim(dimW);
  const frame = document.getElementById('board-frame');
  const maxW = (frame && frame.clientWidth) || 600;
  let w = maxW, h = Math.round(w * aspect);
  // If height exceeds 850px, shrink width to keep aspect ratio within 850px
  if (h > 850) {
    h = 850;
    w = Math.round(h / aspect);
  }
  h = Math.max(300, h);
  const box = document.getElementById('preview-box');
  if (box) {
    box.style.height = h + 'px';
    box.style.width = w < maxW ? w + 'px' : '100%';
  }
  if (glMap) glMap.resize();
}




// Fly to place
async function flyToPlace(name) {
  setStatus('Finding the place\u2026');
  try {
    const geo = await geocodePlace(name, msg => setStatus(msg));
    if (geo.extentKm) {
      const fitKm = Math.max(geo.extentKm.width, geo.extentKm.height) * 1.3;
      const zoom = Math.log2(156543.03 * Math.cos(geo.lat * Math.PI / 180) * (mapContainer.clientWidth || 600) / (fitKm * 1000));
      glMap.flyTo({ center: [geo.lon, geo.lat], zoom: Math.max(3, zoom), duration: 1500 });
    } else {
      glMap.flyTo({ center: [geo.lon, geo.lat], zoom: 12, duration: 1500 });
    }
    setStatus('Adjust the view, then click Generate.', 'ok');
  } catch (err) { setStatus(err.message, 'error'); }
}

document.getElementById('go-btn').addEventListener('click', () => {
  const n = placeInput.value.trim();
  if (n) { showMap(); flyToPlace(n); }
});

document.getElementById('go-btn').addEventListener('click', () => {
  const n = placeInput.value.trim();
  if (n) { showMap(); flyToPlace(n); }
});

placeInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') { const n = placeInput.value.trim(); if (n) { showMap(); flyToPlace(n); } }
});

// Seg handlers
function setupSeg(id, callback) {
  document.getElementById(id).addEventListener('click', (e) => {
    const t = e.target.closest('.sb'); if (!t) return;
    document.getElementById(id).querySelectorAll('.sb').forEach(s => s.classList.toggle('on', s === t));
    callback(t.dataset.value);
  });
}
document.getElementById('road-thin').addEventListener('click', () => {
  roadThickMult = Math.max(0.3, +(roadThickMult - 0.2).toFixed(1));
  Object.keys(layerCache).forEach(k => delete layerCache[k]);
  rerender();
});
document.getElementById('road-thick').addEventListener('click', () => {
  roadThickMult = +(roadThickMult + 0.2).toFixed(1);
  Object.keys(layerCache).forEach(k => delete layerCache[k]);
  rerender();
});
setupSeg('engrave-seg', v => { roadMode = v; Object.keys(layerCache).forEach(k => delete layerCache[k]); rerender(); });
setupSeg('depth-seg', v => { layerCount = parseInt(v, 10); rerender(); });
setupSeg('label-seg', v => { showLabel = v === 'on'; Object.keys(layerCache).forEach(k => delete layerCache[k]); rerender(); });
setupSeg('offset-seg', async v => {
  bandScale = parseFloat(v);
  if (!rawFeatureCache) return;
  const cacheKey = bandScale.toFixed(2);
  if (bandCache[cacheKey]) {
    featureCache = bandCache[cacheKey];
  } else {
    document.getElementById('loading-overlay').style.display = 'flex';
    document.getElementById('loading-msg').textContent = 'Computing offset...';
    await new Promise(r => setTimeout(r, 50));
    const currentAspect = toMmDim(dimH) / toMmDim(dimW);
    featureCache = computeBands(rawFeatureCache, msg => { document.getElementById('loading-msg').textContent = msg; }, currentAspect);
    bandCache[cacheKey] = featureCache;
    document.getElementById('loading-overlay').style.display = 'none';
  }
  Object.keys(layerCache).forEach(k => delete layerCache[k]);
  rerender();
  showSvg();
});
document.getElementById('compass-sz-minus').addEventListener('click', () => {
  compassSzMult = Math.max(0.3, compassSzMult - 0.1);
  rerenderSvgOnly();
});
document.getElementById('compass-sz-plus').addEventListener('click', () => {
  compassSzMult += 0.1;
  rerenderSvgOnly();
});
document.getElementById('label-sz-minus').addEventListener('click', () => {
  labelSzMult = Math.max(0.3, labelSzMult - 0.1);
  rerenderSvgOnly();
});
document.getElementById('label-sz-plus').addEventListener('click', () => {
  labelSzMult += 0.1;
  rerenderSvgOnly();
});
setupSeg('compass-seg', v => { compassCorner = v; Object.keys(layerCache).forEach(k => delete layerCache[k]); rerender(); });
// Frame +/- stepper
const frameSteps = [{label:'Off',val:0},{label:'Thin',val:0.01},{label:'Normal',val:0.02},{label:'Wide',val:0.03},{label:'Extra',val:0.04}];
let frameIdx = 2;
function updateFrameDisplay() {
  document.getElementById('frame-val').textContent = frameSteps[frameIdx].label;
  frameThickness = frameSteps[frameIdx].val;
  showFrame = frameIdx > 0;
  rerenderSvgOnly();
}
document.getElementById('frame-minus').addEventListener('click', () => { frameIdx = Math.max(0, frameIdx - 1); updateFrameDisplay(); });
document.getElementById('frame-plus').addEventListener('click', () => { frameIdx = Math.min(frameSteps.length - 1, frameIdx + 1); updateFrameDisplay(); });

// Title/subtitle update on input
let titleDebounce = null;
function debouncedRerender() { if (titleDebounce) clearTimeout(titleDebounce); titleDebounce = setTimeout(rerenderSvgOnly, 300); }
document.getElementById('title-input').addEventListener('input', debouncedRerender);
document.getElementById('font-select').addEventListener('change', (e) => { titleFont = e.target.value; rerenderSvgOnly(); });
document.getElementById('pad-minus').addEventListener('click', () => { rectPadMult = Math.max(0.3, rectPadMult - 0.2); rerenderSvgOnly(); });
document.getElementById('pad-plus').addEventListener('click', () => { rectPadMult = Math.min(3.0, rectPadMult + 0.2); rerenderSvgOnly(); });
setupSeg('title-backer-seg', v => { titleBackerStyle = v; titleOffsetOn = v === 'offset'; rerenderSvgOnly(); });
document.getElementById('title-sz-minus').addEventListener('click', () => { titleSzMult = Math.max(0.5, titleSzMult - 0.1); rerenderSvgOnly(); });
document.getElementById('title-sz-plus').addEventListener('click', () => { titleSzMult = +(titleSzMult + 0.1).toFixed(1); rerenderSvgOnly(); });
document.getElementById('sub-sz-minus').addEventListener('click', () => { subSzMult = Math.max(0.5, subSzMult - 0.1); rerenderSvgOnly(); });
document.getElementById('sub-sz-plus').addEventListener('click', () => { subSzMult = Math.min(2.0, subSzMult + 0.1); rerenderSvgOnly(); });
function setupHoldBtn(id, fn) {
  const btn = document.getElementById(id);
  let iv = null;
  const go = () => { fn(); rerenderSvgOnly(); };
  const start = () => { go(); iv = setInterval(go, 120); };
  const stop = () => { if (iv) { clearInterval(iv); iv = null; } };
  btn.addEventListener('mousedown', start);
  btn.addEventListener('touchstart', (e) => { e.preventDefault(); start(); });
  btn.addEventListener('mouseup', stop); btn.addEventListener('mouseleave', stop);
  btn.addEventListener('touchend', stop); btn.addEventListener('touchcancel', stop);
}
setupHoldBtn('sub-left', () => { subDx -= 1; });
setupHoldBtn('sub-right', () => { subDx += 1; });
document.getElementById('gap-minus').addEventListener('click', () => { lineGap = Math.max(0.8, lineGap - 0.1); rerenderSvgOnly(); });
document.getElementById('gap-plus').addEventListener('click', () => { lineGap = Math.min(3.0, lineGap + 0.1); rerenderSvgOnly(); });
setupHoldBtn('title-left', () => { titleDx -= 1; });
setupHoldBtn('title-right', () => { titleDx += 1; });
setupHoldBtn('title-up', () => { titleDy -= 1; });
setupHoldBtn('title-down', () => { titleDy += 1; });
document.getElementById('title-reset').addEventListener('click', () => { titleDx = 0; titleDy = 0; rerenderSvgOnly(); });
document.getElementById('subtitle-input').addEventListener('input', debouncedRerender);

// Dimension controls
function updateDimDisplay() {
  document.getElementById('w-val').textContent = dimW + ' ' + dimUnit();
  document.getElementById('h-val').textContent = dimH + ' ' + dimUnit();
  sizeHint.textContent = 'Map \u2248 ' + dimW + ' \u00d7 ' + dimH + ' ' + dimUnit() + ' when cut.';
}
document.getElementById('w-minus').addEventListener('click', () => { dimW = Math.max(dimMin(), dimW - dimStep()); updateDimDisplay(); updateMapAspect(); });
document.getElementById('w-plus').addEventListener('click', () => { dimW = Math.min(dimMax(), dimW + dimStep()); updateDimDisplay(); updateMapAspect(); });
document.getElementById('h-minus').addEventListener('click', () => { dimH = Math.max(dimMin(), dimH - dimStep()); updateDimDisplay(); updateMapAspect(); });
document.getElementById('h-plus').addEventListener('click', () => { dimH = Math.min(dimMax(), dimH + dimStep()); updateDimDisplay(); updateMapAspect(); });

unitSeg.addEventListener('click', (e) => {
  const t = e.target.closest('.sb'); if (!t) return;
  unitSeg.querySelectorAll('.sb').forEach(s => s.classList.toggle('on', s === t));
  const newIn = t.dataset.value === 'in';
  if (newIn !== useInches) {
    dimW = newIn ? Math.max(MIN_DIM_IN, Math.round(dimW / INCHES_TO_MM)) : Math.max(MIN_DIM_MM, Math.round(dimW * INCHES_TO_MM / 10) * 10);
    dimH = newIn ? Math.max(MIN_DIM_IN, Math.round(dimH / INCHES_TO_MM)) : Math.max(MIN_DIM_MM, Math.round(dimH * INCHES_TO_MM / 10) * 10);
    useInches = newIn; updateDimDisplay();
  }
});
updateDimDisplay();
setTimeout(updateMapAspect, 100);

function downloadSvg(str, name) {
  try {
    const blob = new Blob([str], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    // Fallback: open SVG in new tab for manual save
    const w = window.open('', '_blank');
    if (w) { w.document.write('<pre>' + str.replace(/</g, '&lt;') + '</pre>'); }
    else { setStatus('Popup blocked. Allow popups and try again.', 'error'); }
  }
}

// Fast re-render from cached data
function rerenderSvgOnly() {
  if (!lastResult || !featureCache) return;
  const { layers, widthMm, heightMm } = lastResult;
  const refW = featureCache.refSize;
  const refH = featureCache.refSize * (heightMm / widthMm);
  svgContainer.innerHTML = buildFullSvg(layers, refW, refH, 0, 0);
}

const layerCache = {}; // cache buildLayers results per roadMode+layerCount key

function rerender() {
  if (!featureCache) return;
  const widthMm = toMmDim(dimW), heightMm = toMmDim(dimH);
  const cacheKey = roadMode + '_' + layerCount;
  let layers;
  if (layerCache[cacheKey]) {
    layers = layerCache[cacheKey];
  } else {
    layers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode);
    layerCache[cacheKey] = layers;
  }
  lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers };
  const refW = featureCache.refSize;
  const refH = featureCache.refSize * (heightMm / widthMm);
  svgContainer.innerHTML = buildFullSvg(layers, refW, refH, 0, 0);
  dlBtn.disabled = false;
}

// Full generate: extract from map + compute bands + render
generateBtn.addEventListener('click', async () => {
  generateBtn.disabled = true; dlBtn.disabled = true;
  document.getElementById('loading-overlay').style.display = 'flex';
  const loadMsg = document.getElementById('loading-msg');
  try {
    loadMsg.textContent = 'Waiting for tiles\u2026';
    await new Promise((resolve) => {
      if (glMap && glMap.loaded() && glMap.isSourceLoaded('openmaptiles')) { resolve(); return; }
      if (!glMap) { resolve(); return; }
      const onIdle = () => { glMap.off('idle', onIdle); resolve(); };
      glMap.on('idle', onIdle);
      setTimeout(() => { glMap.off('idle', onIdle); resolve(); }, 8000);
    });

    const mapHidden = mapContainer.style.display === 'none';
    const widthKm = (mapHidden && lastWidthKm) ? lastWidthKm : mapWidthKm();
    lastWidthKm = widthKm;
    const widthMm = toMmDim(dimW), heightMm = toMmDim(dimH);

    loadMsg.textContent = 'Extracting features\u2026';
    await new Promise(r => setTimeout(r, 50));

    rawFeatureCache = extractRawFeatures(glMap, widthKm, msg => { loadMsg.textContent = msg; });
    Object.keys(bandCache).forEach(k => delete bandCache[k]); // clear stale bands from any previously generated location
    featureCache = computeBands(rawFeatureCache, msg => { loadMsg.textContent = msg; }, heightMm / widthMm);
    bandCache[bandScale.toFixed(2)] = featureCache;
    Object.keys(layerCache).forEach(k => delete layerCache[k]);

    loadMsg.textContent = 'Building layers\u2026';
    await new Promise(r => setTimeout(r, 50));

    const refW = featureCache.refSize;
    const refH = featureCache.refSize * (heightMm / widthMm);
    const layers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode);
    lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers };
    svgContainer.innerHTML = buildFullSvg(layers, refW, refH, 0, 0);
    dlBtn.disabled = false;
    showSvg();
    document.getElementById('loading-overlay').style.display = 'none';
    setStatus('', 'ok');
  } catch (err) {
    document.getElementById('loading-overlay').style.display = 'none';
    setStatus(err.message || 'Error.', 'error');
  } finally { generateBtn.disabled = false; }
});

// Download
dlBtn.addEventListener('click', async () => {
  if (!lastResult || !featureCache) { setStatus('Generate a map first.', 'error'); return; }
  document.getElementById('loading-overlay').style.display = 'flex';
  document.getElementById('loading-msg').textContent = 'Preparing export...';
  try {
    Object.keys(layerCache).forEach(k => delete layerCache[k]);
    const widthMm = toMmDim(dimW), heightMm = toMmDim(dimH);
    document.getElementById('loading-msg').textContent = 'Rebuilding layers...';
    await new Promise(r => setTimeout(r, 50));
    const freshLayers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode);
    lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers: freshLayers };
    const refW = featureCache.refSize;
    const refH = featureCache.refSize * (heightMm / widthMm);

    document.getElementById('loading-msg').textContent = 'Loading fonts...';
    await new Promise(r => setTimeout(r, 50));
    let titleFont2 = null, interFont2 = null;
    try {
      titleFont2 = await loadFont(titleFont);
      interFont2 = await loadFont("Inter,Helvetica,sans-serif");
    } catch(fe) { console.warn('Font load issue:', fe); }

    document.getElementById('loading-msg').textContent = 'Building SVG...';
    await new Promise(r => setTimeout(r, 50));
    const svgStr = buildLayeredExportSvg(freshLayers, refW, refH, 0, 0, widthMm, heightMm, titleFont2, interFont2);

    if (!svgStr || svgStr.length < 100) {
      setStatus('Export produced empty SVG.', 'error');
      console.error('SVG string length:', svgStr ? svgStr.length : 0);
      return;
    }

    document.getElementById('loading-msg').textContent = 'Downloading...';
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'map-all-layers.svg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    document.getElementById('loading-overlay').style.display = 'none';
    setStatus('SVG downloaded.', 'ok');
  } catch (e) {
    document.getElementById('loading-overlay').style.display = 'none';
    console.error('EXPORT ERROR:', e);
    setStatus('Export error: ' + (e.message || e), 'error');
    alert('Export failed: ' + (e.message || e) + '\n\nCheck browser console for details.');
  }
});

// Environment check
(function() {
  if (window.self !== window.top) {
    try { void window.top.location.href; } catch (_) {
      generateBtn.disabled = true; generateBtn.textContent = 'Requires a browser';
      mapContainer.innerHTML = '<div style="padding:40px;text-align:center;color:#888;">Download and open in Chrome/Firefox/Safari.</div>';
      return;
    }
  }
  if (location.protocol === 'file:') setStatus('Local file \u2014 try Firefox or: python3 -m http.server', 'error');
})();



// Google Analytics event tracking for Layered Map Generator
document.getElementById('generate-btn').addEventListener('click', function() {
  if (typeof gtag === 'function') {
    gtag('event', 'layered_map_generate', {
      event_category: 'Layered Map Generator',
      event_label: document.getElementById('place-input').value || 'unknown'
    });
  }
});

document.getElementById('dl-full').addEventListener('click', function() {
  if (typeof gtag === 'function') {
    gtag('event', 'layered_map_download', {
      event_category: 'Layered Map Generator',
      event_label: document.getElementById('place-input').value || 'unknown',
      value: 1
    });
  }
});